/**
 * Session-only Calculate Geometry for attribute-table rows.
 * Area / length use planar metres when coordinates look like Web Mercator,
 * otherwise a spherical approximation from lon/lat.
 */
import type { VectorExportFeature } from '@geosoft/viewer-sdk';
import { centroidFromGeometry } from './vectorAttributeRows';

export type GeometryProperty = 'area' | 'length' | 'centroidX' | 'centroidY';
export type GeometryUnit = 'm' | 'km' | 'm2' | 'km2' | 'ha';

export const EARTH_RADIUS_M = 6371008.8;

const DEFAULT_FIELDS: Record<GeometryProperty, string> = {
  area: 'AREA',
  length: 'LENGTH',
  centroidX: 'CENTROID_X',
  centroidY: 'CENTROID_Y',
};

export function defaultGeometryField(property: GeometryProperty): string {
  return DEFAULT_FIELDS[property];
}

export function featuresHaveGeometry(rows: ReadonlyArray<VectorExportFeature>): boolean {
  return rows.some((row) => Boolean(row.geometry));
}

export function calculateGeometryValue(
  geometry: unknown,
  property: GeometryProperty,
  unit: GeometryUnit,
): number | null {
  if (!geometry) return null;
  if (property === 'centroidX' || property === 'centroidY') {
    const centroid = centroidFromGeometry(geometry);
    if (!centroid) return null;
    return property === 'centroidX' ? centroid.x : centroid.y;
  }
  if (property === 'length') {
    const metres = geometryLengthMetres(geometry);
    if (metres === null) return null;
    return unit === 'km' ? metres / 1000 : metres;
  }
  const squareMetres = geometryAreaSquareMetres(geometry);
  if (squareMetres === null) return null;
  if (unit === 'km2') return squareMetres / 1_000_000;
  if (unit === 'ha') return squareMetres / 10_000;
  return squareMetres;
}

export function applyGeometryColumn(
  rows: ReadonlyArray<VectorExportFeature>,
  property: GeometryProperty,
  unit: GeometryUnit,
  outputField: string,
): { ok: true; rows: VectorExportFeature[] } | { ok: false; error: string } {
  const name = outputField.trim();
  if (!name) return { ok: false, error: 'Enter an output field name.' };
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) {
    return { ok: false, error: 'Field names must start with a letter or underscore.' };
  }
  if ((property === 'area' && (unit === 'm' || unit === 'km'))
    || (property === 'length' && (unit === 'm2' || unit === 'km2' || unit === 'ha'))) {
    return { ok: false, error: 'Choose a unit that matches the geometry property.' };
  }
  return {
    ok: true,
    rows: rows.map((row) => ({
      ...row,
      properties: {
        ...(row.properties ?? {}),
        [name]: calculateGeometryValue(row.geometry, property, unit),
      },
    })),
  };
}

export function unitsForProperty(property: GeometryProperty): readonly GeometryUnit[] {
  if (property === 'area') return ['m2', 'km2', 'ha'];
  if (property === 'length') return ['m', 'km'];
  return [];
}

function geometryLengthMetres(geometry: unknown): number | null {
  const paths = linePathsOf(geometry);
  if (paths.length === 0) return null;
  let total = 0;
  for (const path of paths) {
    total += pathLengthMetres(path);
  }
  return total;
}

function geometryAreaSquareMetres(geometry: unknown): number | null {
  const polygons = polygonsOf(geometry);
  if (polygons.length === 0) return null;
  let total = 0;
  for (const rings of polygons) {
    if (rings.length === 0) continue;
    total += Math.abs(ringAreaSquareMetres(rings[0]));
    for (let i = 1; i < rings.length; i += 1) {
      total -= Math.abs(ringAreaSquareMetres(rings[i]));
    }
  }
  return total;
}

function pathLengthMetres(path: ReadonlyArray<readonly [number, number]>): number {
  let total = 0;
  for (let i = 1; i < path.length; i += 1) {
    total += segmentLength(path[i - 1], path[i]);
  }
  return total;
}

function ringAreaSquareMetres(ring: ReadonlyArray<readonly [number, number]>): number {
  if (ring.length < 3) return 0;
  if (isMercatorRing(ring)) return planarShoelace(ring);
  return sphericalRingArea(ring);
}

function segmentLength(
  a: readonly [number, number],
  b: readonly [number, number],
): number {
  if (isMercatorPoint(a) || isMercatorPoint(b)) {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    return Math.hypot(dx, dy);
  }
  return haversineMetres(a[0], a[1], b[0], b[1]);
}

function planarShoelace(ring: ReadonlyArray<readonly [number, number]>): number {
  let sum = 0;
  for (let i = 0; i < ring.length - 1; i += 1) {
    sum += ring[i][0] * ring[i + 1][1] - ring[i + 1][0] * ring[i][1];
  }
  return sum / 2;
}

function sphericalRingArea(ring: ReadonlyArray<readonly [number, number]>): number {
  let total = 0;
  for (let i = 0; i < ring.length - 1; i += 1) {
    const lon1 = toRad(ring[i][0]);
    const lon2 = toRad(ring[i + 1][0]);
    const lat1 = toRad(ring[i][1]);
    const lat2 = toRad(ring[i + 1][1]);
    total += (lon2 - lon1) * (2 + Math.sin(lat1) + Math.sin(lat2));
  }
  return (total * EARTH_RADIUS_M * EARTH_RADIUS_M) / 2;
}

function haversineMetres(lon1: number, lat1: number, lon2: number, lat2: number): number {
  const p1 = toRad(lat1);
  const p2 = toRad(lat2);
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dLon / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

function linePathsOf(geometry: unknown): Array<Array<readonly [number, number]>> {
  const record = asRecord(geometry);
  if (!record) return [];
  const type = String(record.type ?? '');
  if (type === 'LineString') return [asPath(record.coordinates)];
  if (type === 'MultiLineString') {
    return asArray(record.coordinates).map(asPath);
  }
  if (type === 'Polygon') {
    const rings = asArray(record.coordinates);
    return rings.length > 0 ? [asPath(rings[0])] : [];
  }
  if (type === 'MultiPolygon') {
    return asArray(record.coordinates).flatMap((polygon) => {
      const rings = asArray(polygon);
      return rings.length > 0 ? [asPath(rings[0])] : [];
    });
  }
  return [];
}

function polygonsOf(geometry: unknown): Array<Array<Array<readonly [number, number]>>> {
  const record = asRecord(geometry);
  if (!record) return [];
  const type = String(record.type ?? '');
  if (type === 'Polygon') return [asArray(record.coordinates).map(asPath)];
  if (type === 'MultiPolygon') {
    return asArray(record.coordinates).map((polygon) => asArray(polygon).map(asPath));
  }
  return [];
}

function asPath(value: unknown): Array<readonly [number, number]> {
  return asArray(value)
    .map((point) => {
      if (!Array.isArray(point) || typeof point[0] !== 'number' || typeof point[1] !== 'number') {
        return null;
      }
      return [point[0], point[1]] as const;
    })
    .filter((point): point is readonly [number, number] => point !== null);
}

function isMercatorRing(ring: ReadonlyArray<readonly [number, number]>): boolean {
  return ring.some((point) => isMercatorPoint(point));
}

function isMercatorPoint(point: readonly [number, number]): boolean {
  return Math.abs(point[0]) > 180 || Math.abs(point[1]) > 90;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function toRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}
